import React, { Component, useState, useEffect, memo } from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import { Button, Input, CustomInput, UncontrolledTooltip } from "reactstrap";
import Creatable from "../../../components/common/CreatableSelect";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import { AddCommaNumber, CleanPrice } from "../../../helpers/Utils";
import { SetIceOrSugar } from "../../../helpers/Product";
import ProductVariantConfigModal from "./ProductVariantConfigModal";
import RecipeConfigModal from "./RecipeConfigModal";
import Pagination from "../../../components/DatatablePagination";
/* LEGACY_PRODUCT_ENTRY_PERFECT_SCROLLBAR_IMPORT_START
import PerfectScrollbar from "react-perfect-scrollbar";
LEGACY_PRODUCT_ENTRY_PERFECT_SCROLLBAR_IMPORT_END */
import "react-perfect-scrollbar/dist/css/styles.css";
import { v4 as uuidv4 } from "uuid";
import { NotificationManager } from "../../../components/common/react-notifications";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";
import HorizontalScrollAffordance from "../../../components/common/HorizontalScrollAffordance";
import { hasInventoryValue } from "../../../views/app/pages/product/productVariantHandlers";

// Optimized Cell Components
const EditableCell = memo(
  ({
    value,
    onChange,
    onBlur,
    onKeyDown,
    type = "text",
    placeholder = "",
    disabled = false,
    ...rest
  }) => {
    const [localValue, setLocalValue] = useState(value ?? "");

    useEffect(() => {
      setLocalValue(value ?? "");
    }, [value]);

    const handleChange = (e) => setLocalValue(e.target.value);

    const handleBlur = () => {
      if (localValue !== value) onChange(localValue);
    };

    return (
      <Input
        type={type}
        bsSize="sm"
        value={localValue}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        style={type === "number" ? { textAlign: "right" } : {}}
        {...rest}
      />
    );
  },
);

const NumberEditableCell = memo(
  ({ value, onChange, placeholder = "0", disabled = false }) => {
    const [displayValue, setDisplayValue] = useState(
      value ? AddCommaNumber(value) : "",
    );

    useEffect(() => {
      setDisplayValue(value ? AddCommaNumber(value) : "");
    }, [value]);

    const handleChange = (e) => {
      const inputValue = e.target.value;
      const rawNumber = inputValue.replace(/\./g, "");
      if (rawNumber === "" || /^\d+$/.test(rawNumber)) {
        setDisplayValue(rawNumber ? AddCommaNumber(rawNumber) : "");
      }
    };

    const handleBlur = () => {
      const rawNumber = displayValue.replace(/\./g, "");
      if (rawNumber !== CleanPrice(value)) {
        onChange(rawNumber);
      }
    };

    return (
      <Input
        type="text"
        bsSize="sm"
        value={displayValue}
        placeholder={placeholder}
        style={{ textAlign: "right" }}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
      />
    );
  },
);

const CategorySelectCell = injectIntl(
  memo(({ value, options, onChange, intl }) => {
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
      let option = options.find((c) => c.value === value);
      if (!option && value) {
        // If value exists but not in options, treat as custom/new entry
        option = { label: value, value: value };
      }
      setSelectedOption(option || null);
    }, [value, options]);

    const handleChange = (opt) => {
      setSelectedOption(opt);
      onChange(opt);
    };

    return (
      <div style={{ width: "100%" }}>
        <Creatable
          className="react-select"
          classNamePrefix="react-select"
          options={options}
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          value={selectedOption}
          onChange={handleChange}
          placeholder={intl.formatMessage({ id: "label.select-or-create" })}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: "label.create-new" })}: "${inputValue}"`
          }
          createOptionPosition="first"
        />
      </div>
    );
  }),
);

// Creatable Select cell cho nhà cung cấp
const SupplierSelectCell = injectIntl(
  memo(({ value, onChange, options = [], isDisabled = false, intl }) => {
    const [localValue, setLocalValue] = useState(
      value ? { value: value, label: value } : null,
    );

    useEffect(() => {
      setLocalValue(value ? { value: value, label: value } : null);
    }, [value]);

    const handleChange = (selected) => {
      setLocalValue(selected);
      onChange(
        selected ? (selected.__isNew__ ? selected.label : selected.value) : "",
      );
    };

    const seen = new Set();
    const selectOptions = (options || [])
      .filter(
        (opt) =>
          opt &&
          (typeof opt === "string" ||
            (typeof opt === "object" && (opt.label || opt.name))),
      )
      .map((opt) => {
        if (typeof opt === "string") return { value: opt, label: opt };
        return {
          value: opt.name || opt.label,
          label: opt.name || opt.label,
        };
      })
      .filter((opt) => {
        if (seen.has(opt.value)) return false;
        seen.add(opt.value);
        return true;
      });

    return (
      <div style={{ width: "100%" }}>
        <Creatable
          value={localValue}
          onChange={handleChange}
          options={selectOptions}
          placeholder={intl.formatMessage({
            id: "label.select-create-provider",
          })}
          isClearable
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: "label.create-new" })} "${inputValue}"`
          }
          className="react-select"
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          isDisabled={isDisabled}
        />
      </div>
    );
  }),
);

class ProductEntryModal extends Component {
  constructor(props) {
    super(props);

    // Initial Data Generation - 15 hàng mặc định
    const initialData =
      this.props.savedData && this.props.savedData.length > 0
        ? this.props.savedData
        : Array.from({ length: 15 }).map(() => this.getEmptyRow());

    this.state = {
      data: initialData,
      categories: [], // Formatted for Select
      createdProviders: [], // Nhà cung cấp mới tạo trong phiên này
      modalOpen: false,
      recipeModalOpen: false,
      currentRowIndex: null,
      currentRowData: null,
      currentRecipeData: null,
      scrollContentWidth: 0,
      scrollClientWidth: 0,
    };

    this.scrollRef = null;
    this.topScrollRef = React.createRef();
    this.tableInnerRef = React.createRef();
    this.dragScrollState = null;
    this.fileInputRef = React.createRef();
    this.currentUploadRowIndex = null;

    // Maps for Lookup
    this.categoryMap = new Map(); // Name -> ID
    this.reverseCategoryMap = new Map(); // ID -> Name (helper)
    this.barcodeMap = new Map(); // Barcode -> Product Data

    const { business_type } = this.props;
    let unitPlaceholder = this.props.intl.formatMessage({
      id: "unit.placeholder.default",
    });
    if (business_type === "cafe" || business_type === "restaurant") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.cafe",
      });
    } else if (business_type === "fashion") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.fashion",
      });
    } else if (business_type === "cosmetics" || business_type === "beauty") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.cosmetics",
      });
    } else if (business_type === "retail" || business_type === "grocery") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.retail",
      });
    } else if (business_type === "pharmacy") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.pharmacy",
      });
    } else if (business_type === "electronics") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.electronics",
      });
    } else if (business_type === "spa" || business_type === "salon") {
      unitPlaceholder = this.props.intl.formatMessage({
        id: "unit.placeholder.spa",
      });
    }

    // Define columns once to avoid re-render issues losing focus
    // Barcode column sẽ được ẩn cho cafe/restaurant trong getColumns()
    const { messages } = this.props.intl;
    this.allColumns = [
      {
        Header: <IntlMessages id="modal.stock.stt" />,
        width: 40,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (row) => <div className="text-center">{row.index + 1}</div>,
      },
      {
        Header: <IntlMessages id="label.enter-barcode" />,
        accessor: "barcode",
        width: 150,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <EditableCell
            value={props.value}
            placeholder="8-13 chữ số"
            onChange={(val) =>
              this.handleCellChange(props.index, "barcode", val)
            }
            onKeyDown={(e) => this.handleBarcodeKeyDown(e, props.index)}
          />
        ),
      },
      {
        Header: (
          <span>
            <IntlMessages id="product.name" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "name",
        minWidth: 200,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <EditableCell
            value={props.value}
            placeholder={this.props.intl.formatMessage({
              id: "label.enter-product-name",
            })}
            onChange={(val) => this.handleCellChange(props.index, "name", val)}
            list="product-suggestions"
          />
        ),
      },
      {
        Header: (
          <span>
            <IntlMessages id="stock.unit" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "unit",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        Cell: (props) => (
          <div style={{ width: "100%" }}>
            <EditableCell
              id={`unit-input-${props.index}`}
              value={props.value}
              placeholder={unitPlaceholder}
              onChange={(val) =>
                this.handleCellChange(props.index, "unit", val)
              }
            />
            <UncontrolledTooltip
              placement="right"
              target={`unit-input-${props.index}`}
              trigger="focus"
            >
              <IntlMessages id="tooltip.unit-hint" />
            </UncontrolledTooltip>
          </div>
        ),
      },

      {
        Header: (
          <span>
            <IntlMessages id="pages.category" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "category_id",
        width: 220,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        Cell: (props) => (
          <CategorySelectCell
            value={props.value}
            options={this.state.categories}
            onChange={(opt) => {
              if (opt && opt.__isNew__) {
                this.setState((prev) => {
                  const exists = prev.categories.some(
                    (c) =>
                      String(c.label || "").toLowerCase() ===
                      String(opt.label || "").toLowerCase(),
                  );
                  if (!exists) {
                    return { categories: [...prev.categories, opt] };
                  }
                  return null;
                });
              }

              const newData = [...this.state.data];
              // Nếu là option mới được tạo, value sẽ là label
              newData[props.index].category_id = opt
                ? opt.__isNew__
                  ? opt.label
                  : opt.value
                : null;
              newData[props.index].category_name = opt ? opt.label : "";
              newData[props.index].is_new_category = opt
                ? opt.__isNew__ || false
                : false;
              this.setState({ data: newData });
            }}
          />
        ),
      },
      {
        Header: (
          <span>
            <IntlMessages id="product.price" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "price",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => {
          const row = props.original;
          const price = parseFloat(row.price || 0);
          const priceSale = parseFloat(row.price_sale || 0);
          const shouldStrike = priceSale > 0 && priceSale < price;

          return (
            <NumberEditableCell
              value={props.value}
              placeholder="0"
              style={{
                textDecoration: shouldStrike ? "line-through" : "none",
                color: shouldStrike ? "#999" : "inherit",
              }}
              onChange={(val) => {
                this.handleCellChange(props.index, "price", val);

                const p = parseFloat(val || 0);
                const ps = parseFloat(row.price_sale || 0);
                this.handleCellChange(
                  props.index,
                  "active_sale",
                  ps > 0 && ps < p,
                );
              }}
            />
          );
        },
      },
      {
        Header: <IntlMessages id="product.promotion-price" />,
        accessor: "price_sale",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <NumberEditableCell
            value={props.value}
            placeholder="0"
            onChange={(val) => {
              this.handleCellChange(props.index, "price_sale", val);

              const ps = parseFloat(val || 0);
              const p = parseFloat(props.original.price || 0);
              this.handleCellChange(
                props.index,
                "active_sale",
                ps > 0 && ps < p,
              );
            }}
          />
        ),
      },
      {
        Header: <IntlMessages id="product.price.wholesale" />,
        accessor: "price_wholesale",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <NumberEditableCell
            value={props.value}
            placeholder="0"
            onChange={(val) => {
              this.handleCellChange(props.index, "price_wholesale", val);
            }}
          />
        ),
      },
      {
        Header: <IntlMessages id="product.tax-rate" defaultMessage="Thuế (%)" />,
        accessor: "tax_rate",
        width: 100,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <NumberEditableCell
            value={props.value ?? 0}
            placeholder="0"
            onChange={(val) => {
              this.handleCellChange(props.index, "tax_rate", val);
            }}
          />
        ),
      },
      {
        Header: (
          <IntlMessages id="product.multi.recipe" defaultMessage="Định lượng" />
        ),
        id: "recipe",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => {
          const { ingredients } = props.original;
          const count = ingredients?.length || 0;
          return (
            <Button
              color="outline-info"
              size="sm"
              block
              onClick={() => this.openRecipe(props.index)}
            >
              {count > 0
                ? `${count} ${messages["menu.product.recipe"] || "NL"}`
                : messages["product.multi.recipe"] || "Định lượng"}
            </Button>
          );
        },
      },

      {
        Header: (
          <span>
            <IntlMessages id="product.price.cost" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "stock_cost_price",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <div
            id={`cost-price-wrapper-${props.index}`}
            style={{ width: "100%" }}
          >
            <NumberEditableCell
              value={props.value}
              placeholder="0"
              // Vô hiệu hóa nếu là hàng Combo
              disabled={props.original.is_combo}
              onChange={(val) =>
                this.handleCellChange(props.index, "stock_cost_price", val)
              }
            />
            {props.original.is_combo && (
              <UncontrolledTooltip
                placement="top"
                target={`cost-price-wrapper-${props.index}`}
              >
                <IntlMessages id="tooltip.not-for-combo" />
              </UncontrolledTooltip>
            )}
          </div>
        ),
      },
      {
        Header: (
          <span>
            <IntlMessages id="warehouse.import-quantity" />{" "}
            <span style={{ color: "red" }}>*</span>
          </span>
        ),
        accessor: "stock_quantity",
        width: 150,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <div id={`quantity-wrapper-${props.index}`} style={{ width: "100%" }}>
            <NumberEditableCell
              value={props.value}
              placeholder="0"
              // Vô hiệu hóa nếu là hàng Combo
              disabled={props.original.is_combo}
              onChange={(val) =>
                this.handleCellChange(props.index, "stock_quantity", val)
              }
            />
            {props.original.is_combo && (
              <UncontrolledTooltip
                placement="top"
                target={`quantity-wrapper-${props.index}`}
              >
                <IntlMessages id="tooltip.not-for-combo" />
              </UncontrolledTooltip>
            )}
          </div>
        ),
      },
      {
        Header: <IntlMessages id="modal.product.combo" />,
        accessor: "is_combo",
        width: 80,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => {
          const row = props.original;
          const count = row.ingredients ? row.ingredients.length : 0;

          if (count > 0) {
            return (
              <Button
                color="outline-info"
                size="sm"
                className="py-1 px-2"
                style={{
                  borderRadius: "12px",
                  fontSize: "11px",
                  minWidth: "60px",
                }}
                onClick={() => this.openRecipe(props.index, false)}
                title={messages["product.edit_cost"]}
              >
                {count} NL
              </Button>
            );
          }

          return (
            <div className="text-center" id={`combo-wrapper-${props.index}`}>
              <CustomInput
                type="checkbox"
                id={`combo-${props.index}`}
                checked={props.value || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  this.handleCellChange(props.index, "is_combo", isChecked);
                  // Nếu bật Combo: Clear Số lượng nhập, Giá vốn và MỞ MODAL ĐỊNH LƯỢNG
                  if (isChecked) {
                    this.handleCellChange(props.index, "stock_quantity", "");
                    this.handleCellChange(props.index, "stock_cost_price", "");
                    this.handleCellChange(props.index, "stock_paid", "");
                    // Mở modal định lượng ngay lập tức (Auto Open)
                    setTimeout(() => this.openRecipe(props.index, true), 100);
                  }
                }}
              />
              <UncontrolledTooltip
                placement="top"
                target={`combo-wrapper-${props.index}`}
              >
                <IntlMessages id="tooltip.enable-combo" />
              </UncontrolledTooltip>
            </div>
          );
        },
      },
      {
        Header: (
          <IntlMessages id="modal.product.variant" defaultMessage="Biến thể" />
        ),
        id: "config",
        width: 120,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => {
          const { business_type } = this.props;
          const isFnB =
            business_type === "cafe" || business_type === "restaurant";
          const { sizes, toppings, color, material } = props.original;
          const count =
            (sizes?.length || 0) +
            (toppings?.length || 0) +
            (color?.length || 0) +
            (material?.length || 0);
          return (
            <div
              id={`variant-wrapper-${props.index}`}
              style={{ width: "100%" }}
            >
              <Button
                color="outline-primary"
                size="sm"
                block
                disabled={props.original.is_combo && !isFnB}
                onClick={() => this.openConfig(props.index)}
                style={
                  props.original.is_combo && !isFnB
                    ? { pointerEvents: "none" }
                    : {}
                }
              >
                {count > 0
                  ? `${count} ${messages["modal.product.variant"]}`
                  : messages["modal.product.variant"]}
              </Button>
              {props.original.is_combo && !isFnB && (
                <UncontrolledTooltip
                  placement="top"
                  target={`variant-wrapper-${props.index}`}
                >
                  <IntlMessages id="tooltip.not-for-combo" />
                </UncontrolledTooltip>
              )}
            </div>
          );
        },
      },
      {
        Header: <IntlMessages id="modal.stock.supplier" />,
        accessor: "stock_supplier",
        minWidth: 160,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        Cell: (props) => (
          <div id={`supplier-wrapper-${props.index}`} style={{ width: "100%" }}>
            <SupplierSelectCell
              value={props.value}
              onChange={(val) => {
                this.handleCellChange(props.index, "stock_supplier", val);
                if (
                  val &&
                  !(this.props.data_providers || []).some(
                    (p) => (p.name || p.label || p) === val,
                  ) &&
                  !this.state.createdProviders.includes(val)
                ) {
                  this.setState((prev) => ({
                    createdProviders: [...prev.createdProviders, val],
                  }));
                }
              }}
              options={[
                ...(this.props.data_providers || []),
                ...this.state.createdProviders.map((name) => ({
                  name,
                  label: name,
                })),
              ]}
              isDisabled={props.original.is_combo}
            />
            {props.original.is_combo && (
              <UncontrolledTooltip
                placement="top"
                target={`supplier-wrapper-${props.index}`}
              >
                <IntlMessages id="tooltip.not-for-combo" />
              </UncontrolledTooltip>
            )}
          </div>
        ),
      },
      {
        Header: <IntlMessages id="modal.product.paid" />,
        accessor: "stock_paid",
        width: 110,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <div id={`paid-wrapper-${props.index}`} style={{ width: "100%" }}>
            <NumberEditableCell
              value={props.value}
              placeholder="0"
              disabled={props.original.is_combo}
              onChange={(val) =>
                this.handleCellChange(props.index, "stock_paid", val)
              }
            />
            {props.original.is_combo && (
              <UncontrolledTooltip
                placement="top"
                target={`paid-wrapper-${props.index}`}
              >
                <IntlMessages id="tooltip.not-for-combo" />
              </UncontrolledTooltip>
            )}
          </div>
        ),
      },
      {
        Header: <IntlMessages id="modal.stock.remaining" />,
        accessor: "stock_remaining",
        width: 100,
        headerStyle: { textAlign: "right" },
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => {
          if (props.original.is_combo) {
            return (
              <div
                id={`remaining-wrapper-${props.index}`}
                className="text-right text-muted"
                style={{ padding: "6px", width: "100%" }}
              >
                -
                <UncontrolledTooltip
                  placement="top"
                  target={`remaining-wrapper-${props.index}`}
                >
                  <IntlMessages id="tooltip.not-for-combo" />
                </UncontrolledTooltip>
              </div>
            );
          }
          const remaining = this.calculateRemaining(
            props.original.stock_cost_price,
            props.original.stock_quantity,
            props.original.stock_paid,
          );
          return (
            <div
              className={`text-right ${
                remaining > 0 ? "text-danger" : "text-success"
              }`}
              style={{
                padding: "6px",
                fontWeight: remaining > 0 ? "bold" : "normal",
                width: "100%",
              }}
            >
              {remaining !== 0 ? AddCommaNumber(remaining) : "-"}
            </div>
          );
        },
      },
      {
        Header: <IntlMessages id="modal.product.image" />,
        accessor: "image",
        width: 250,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerStyle: { textAlign: "center" }, // Center header text
        Cell: (props) => {
          const images = (
            Array.isArray(props.value)
              ? props.value
              : props.value
                ? [props.value]
                : []
          ).filter(
            (img) =>
              img &&
              String(img).trim() !== "" &&
              String(img) !== "null" &&
              String(img) !== "undefined" &&
              !String(img).includes("default") &&
              !String(img).includes("placeholder"),
          );
          const hasImages = images.length > 0;
          return (
            <div
              className={`d-flex align-items-center ${
                !hasImages ? "justify-content-center" : ""
              }`}
              style={{
                overflowX: "auto",
                maxWidth: "100%",
                position: "relative",
                width: "100%", // Ensure full width for centering
                height: "100%", // Consistent height
              }}
            >
              <div
                className="d-flex"
                style={hasImages ? { paddingRight: "40px" } : {}}
              >
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="position-relative mr-2"
                    style={{ minWidth: "35px" }}
                  >
                    <img
                      src={img}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "none";
                        }
                      }}
                      style={{
                        width: "35px",
                        height: "35px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                    <span
                      className="position-absolute"
                      style={{
                        top: "-8px",
                        right: "-8px",
                        cursor: "pointer",
                        background: "#ff0000",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        fontSize: "10px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      }}
                      onClick={() => this.removeImage(props.index, idx)}
                      title="Xóa ảnh"
                    >
                      x
                    </span>
                  </div>
                ))}
              </div>

              <div
                className="text-center cursor-pointer ml-1"
                onClick={() => this.triggerImageUpload(props.index)}
                style={{
                  width: "35px",
                  height: "35px",
                  border: "1px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  flexShrink: 0,
                  marginTop: "0px",
                  position: hasImages ? "sticky" : "static", // Only sticky if there are images
                  right: 0,
                  background: "#fff", // Opaque background
                  zIndex: 5,
                  boxShadow: hasImages ? "-2px 0 5px rgba(0,0,0,0.1)" : "none", // Only shadow if sticky
                }}
                title="Thêm ảnh"
              >
                <i
                  className="simple-icon-plus text-muted"
                  style={{ fontSize: "10px" }}
                />
              </div>
            </div>
          );
        },
      },
      {
        Header: "Hot",
        accessor: "bestter",
        width: 50,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <div className="text-center">
            <CustomInput
              type="checkbox"
              id={`hot-${props.index}`}
              checked={props.value}
              onChange={(e) =>
                this.handleCellChange(props.index, "bestter", e.target.checked)
              }
            />
          </div>
        ),
      },
      {
        Header: "Xóa",
        width: 50,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        Cell: (props) => (
          <div className="text-center">
            <i
              className="simple-icon-trash text-danger cursor-pointer"
              onClick={() => this.handleRemoveRow(props.index)}
            />
          </div>
        ),
      },
    ];
  }

  // Lấy columns dựa trên business_type
  getColumns = () => {
    const { business_type } = this.props;
    const isFnB = business_type === "cafe" || business_type === "restaurant";

    let columns = this.allColumns;

    if (isFnB) {
      // F&B: Hide Barcode, Stock Quantity, Stock Cost, Supplier, Paid, Remaining, Combo/SX. Show Recipe.
      const stockColumnsToHide = [
        "stock_cost_price",
        "stock_quantity",
        "stock_supplier",
        "stock_paid",
        "stock_remaining",
        "barcode",
        "is_combo", // Hide Combo checkbox for FnB
        "unit", // Hide Unit column for FnB
      ];
      columns = columns.filter(
        (col) =>
          !stockColumnsToHide.includes(col.accessor) &&
          !stockColumnsToHide.includes(col.id),
      );
      // Removed filter for 'unit' to allow formatting for FnB
    } else {
      // Non-F&B: Hide "Recipe" column (use Combo/SX checkbox instead). Show Stock columns.
      columns = columns.filter((col) => col.id !== "recipe");
    }

    return columns;
  };

  setScrollRef = (el) => {
    if (this.scrollRef) {
      const oldContainer = this.getScrollContainer();
      if (oldContainer && oldContainer.removeEventListener) {
        oldContainer.removeEventListener("scroll", this.handleTableScroll);
      }
    }
    this.scrollRef = el;
    const container = this.getScrollContainer();
    if (container && container.addEventListener) {
      container.addEventListener("scroll", this.handleTableScroll, {
        passive: true,
      });
      window.setTimeout(this.syncScrollMetrics, 0);
    }
  };

  getScrollContainer = () => {
    if (!this.scrollRef) return null;
    return this.scrollRef._container || this.scrollRef;
  };

  syncScrollMetrics = () => {
    const container = this.getScrollContainer();
    if (!container) return;

    const scrollContentWidth = container.scrollWidth || 0;
    const scrollClientWidth = container.clientWidth || 0;

    if (
      scrollContentWidth !== this.state.scrollContentWidth ||
      scrollClientWidth !== this.state.scrollClientWidth
    ) {
      this.setState({ scrollContentWidth, scrollClientWidth });
    }

    if (this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
  };

  handleTableScroll = () => {
    const container = this.getScrollContainer();
    if (container && this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
  };

  handleTopScroll = (event) => {
    const container = this.getScrollContainer();
    if (container) {
      container.scrollLeft = event.target.scrollLeft;
    }
  };

  handleScrollBy = (distance) => {
    const container = this.getScrollContainer();
    if (!container) return;

    container.scrollTo({
      left: container.scrollLeft + distance,
      behavior: "smooth",
    });

    window.setTimeout(this.syncScrollMetrics, 250);
  };

  isInteractiveTableTarget = (target) => {
    return Boolean(
      target.closest(
        "input, textarea, select, button, a, .react-select, .custom-control, .dropdown, [role='button']",
      ),
    );
  };

  handleDragScrollStart = (event) => {
    const container = this.getScrollContainer();
    if (
      !container ||
      container.scrollWidth <= container.clientWidth ||
      this.isInteractiveTableTarget(event.target)
    ) {
      return;
    }

    this.dragScrollState = {
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
      moved: false,
    };

    document.addEventListener("mousemove", this.handleDragScrollMove);
    document.addEventListener("mouseup", this.handleDragScrollEnd);
  };

  handleDragScrollMove = (event) => {
    if (!this.dragScrollState) return;
    const container = this.getScrollContainer();
    if (!container) return;

    const distance = event.clientX - this.dragScrollState.startX;
    if (Math.abs(distance) > 4) {
      this.dragScrollState.moved = true;
      container.classList.add("is-dragging-horizontal");
    }

    container.scrollLeft = this.dragScrollState.scrollLeft - distance;
    if (this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
    event.preventDefault();
  };

  handleDragScrollEnd = () => {
    const container = this.getScrollContainer();
    if (container) {
      container.classList.remove("is-dragging-horizontal");
    }
    this.dragScrollState = null;
    document.removeEventListener("mousemove", this.handleDragScrollMove);
    document.removeEventListener("mouseup", this.handleDragScrollEnd);
  };

  // Tính toán còn lại = (giá vốn * số lượng nhập) - đã trả
  calculateRemaining = (price, quantity, paid) => {
    const total =
      (parseFloat(CleanPrice(price)) || 0) *
      (parseFloat(CleanPrice(quantity)) || 0);
    const paidAmount = parseFloat(CleanPrice(paid)) || 0;
    return total - paidAmount;
  };

  componentDidMount() {
    this.processCategories(this.props.categories);
    this.buildBarcodeMap(this.props.data_all);
    window.addEventListener("resize", this.syncScrollMetrics);
    window.setTimeout(this.syncScrollMetrics, 0);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.categories !== this.props.categories) {
      this.processCategories(this.props.categories);
    }
    if (prevProps.data_all !== this.props.data_all) {
      this.buildBarcodeMap(this.props.data_all);
    }
    if (
      prevProps.savedData !== this.props.savedData &&
      this.props.savedData &&
      this.props.savedData.length > 0
    ) {
      this.setState({ data: this.props.savedData });
    }
    if (
      prevProps.savedData !== this.props.savedData ||
      prevProps.business_type !== this.props.business_type ||
      this.tableInnerRef.current
    ) {
      window.setTimeout(this.syncScrollMetrics, 0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.syncScrollMetrics);
    document.removeEventListener("mousemove", this.handleDragScrollMove);
    document.removeEventListener("mouseup", this.handleDragScrollEnd);
    if (this.scrollRef) {
      const container = this.scrollRef._container || this.scrollRef;
      if (container && container.removeEventListener) {
        container.removeEventListener("wheel", this.handleWheel);
        container.removeEventListener("scroll", this.handleTableScroll);
      }
    }
    if (this.props.onDataChange) {
      this.props.onDataChange(this.state.data);
    }
  }

  getEmptyRow = () => {
    const { business_type } = this.props;
    let defaultIce = [];
    let defaultSugar = [];

    // Pre-fill Ice/Sugar for Cafe
    if (business_type === "cafe") {
      const allOptions = SetIceOrSugar().map((o) => o.value);
      defaultIce = allOptions;
      defaultSugar = allOptions;
    }

    return {
      name: "",
      unit: "",
      barcode: "",
      category_id: null,
      category_name: "",
      price: "",
      price_sale: "",
      price_wholesale: "",
      active_sale: false,
      image: "",
      bestter: false,
      softHide: false,
      sizes: [],
      toppings: [],
      ice: defaultIce,
      sugar: defaultSugar,
      ingredients: [],
      stock_quantity: "",
      stock_cost_price: "",
      stock_supplier: "",
      stock_supplier: "",
      stock_paid: "",
      is_combo: false, // Default false
    };
  };

  processCategories = (categories = []) => {
    const formatted = categories.map((c) => ({ label: c.value, value: c.id }));
    this.categoryMap.clear();
    this.reverseCategoryMap.clear();
    categories.forEach((c) => {
      this.categoryMap.set(c.value, c.id);
      this.reverseCategoryMap.set(c.id, c.value);
    });
    this.setState({ categories: formatted });
  };

  buildBarcodeMap = (data) => {
    this.barcodeMap.clear();
    if (data && data.length > 0) {
      data.forEach((p) => {
        if (p.barcode) this.barcodeMap.set(String(p.barcode).trim(), p);
      });
    }
  };

  // --- Data Handling ---
  handleCellChange = (index, field, value) => {
    const newData = [...this.state.data];
    newData[index][field] = value;
    this.setState({ data: newData });
  };

  handleRemoveRow = (index) => {
    const newData = this.state.data.filter((_, i) => i !== index);
    this.setState({ data: newData });
  };

  handleAddRow = () => {
    this.setState((prev) => ({
      data: [...prev.data, this.getEmptyRow()],
    }));
  };

  // --- Image Upload ---
  triggerImageUpload = (index) => {
    this.currentUploadRowIndex = index;
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
      this.fileInputRef.current.click();
    }
  };

  onFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0 || this.currentUploadRowIndex === null) return;

    // Use Promise.all to read all files
    Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }),
    ).then((imagesBase64) => {
      const newData = [...this.state.data];
      // Append new images to existing ones (if any)
      const currentImages = newData[this.currentUploadRowIndex].image
        ? Array.isArray(newData[this.currentUploadRowIndex].image)
          ? newData[this.currentUploadRowIndex].image
          : [newData[this.currentUploadRowIndex].image]
        : [];

      newData[this.currentUploadRowIndex].image = [
        ...currentImages,
        ...imagesBase64,
      ];
      this.setState({ data: newData });
    });
  };

  removeImage = (rowIndex, imgIndex) => {
    const newData = [...this.state.data];
    const currentImages = Array.isArray(newData[rowIndex].image)
      ? newData[rowIndex].image
      : [];
    newData[rowIndex].image = currentImages.filter((_, i) => i !== imgIndex);
    this.setState({ data: newData });
  };

  // --- Barcode Logic ---
  handleBarcodeKeyDown = async (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const barcode = String(e.target.value ?? "").trim();
      if (!barcode) return;

      const product = this.barcodeMap.get(barcode);
      const newData = [...this.state.data];
      const row = newData[index];

      if (product) {
        // Found in store product: Fill data
        row.name = product.name;
        row.price = product.price;
        row.price_sale = product.price_sale;
        row.price_wholesale = product.price_wholesale;
        row.active_sale = product.active_sale;
        row.bestter = product.bestter;
        row.softHide = product.softHide;
        row.image = product.image || "";

        // Category Mapping
        if (product.category) {
          row.category_id = product.category.id;
          row.category_name = product.category.name;
        }

        // Reset Variants
        row.sizes = [];
        row.toppings = [];
        row.ice = [];
        row.sugar = [];
      } else {
        // Not in store product: tra cứu từ sản phẩm mẫu (ProductTemplate)
        try {
          const token = localStorage.getItem("access_token");
          const response = await axios.get(
            `${END_POINT}/product-template/lookup/${encodeURIComponent(barcode)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data && response.data.found && response.data.data) {
            const tmpl = response.data.data;
            row.name = tmpl.name;
            row.price = tmpl.suggested_price && Number(tmpl.suggested_price) > 0 ? String(Math.round(Number(tmpl.suggested_price))) : "";
            row.price_wholesale = tmpl.suggested_cost_price && Number(tmpl.suggested_cost_price) > 0 ? String(Math.round(Number(tmpl.suggested_cost_price))) : "";
            row.image = tmpl.image || "";
            row.barcode = tmpl.barcode || barcode;
            row.brand_name = tmpl.brand_name || "";
            if (tmpl.category_name) {
              row.category_name = tmpl.category_name;
              const matchedCat = (this.props.data_categories || []).find(
                (c) => (c.name || c.label || "").toLowerCase() === tmpl.category_name.toLowerCase()
              );
              if (matchedCat) {
                row.category_id = matchedCat.id || matchedCat.value;
              }
            }
            NotificationManager.success(
              `Tìm thấy sản phẩm mẫu: ${tmpl.name}`,
              "Mã vạch",
              3000,
              null,
              null,
              "filled"
            );
          }
        } catch (err) {
          console.warn("Lỗi tra cứu barcode template trong ModalAddProducts:", err);
        }
      }

      // Auto-add row if at last row
      if (index === this.state.data.length - 1) {
        newData.push(this.getEmptyRow());
      }

      this.setState({ data: newData });
    }
  };

  // --- Variant Config ---
  toggleModal = () => {
    this.setState((prevState) => ({
      modalOpen: !prevState.modalOpen,
    }));
  };

  openConfig = (index) => {
    const { data } = this.state;
    const { business_type } = this.props;
    const row = data[index];
    const isRetail = business_type !== "cafe" && business_type !== "restaurant";

    if (isRetail && (!row.unit || row.unit.trim() === "")) {
      NotificationManager.warning(
        this.props.intl.formatMessage({
          id: "product.alert.create-variant-missing-unit",
        }),
        this.props.intl.formatMessage({ id: "modal.alert.warning" }),
        3000,
      );
      return;
    }

    this.setState({
      modalOpen: true,
      currentRowIndex: index,
      currentRowData: {
        sizes: row.sizes || [],
        toppings: row.toppings || [],
        ice: row.ice || [],
        sugar: row.sugar || [],
        // Map for Fashion/Retail/Cosmetics
        size: row.sizes || [], // Pass existing sizes (as tags) to 'size' prop
        color: row.color || [],
        material: row.material || [],
        variants: row.variants || [],
        price: row.price,
        stock_cost_price: row.stock_cost_price, // Pass cost price for auto-calculate
        barcode: row.barcode,
        sku: row.sku,
        unit: row.unit,
      },
    });
  };

  handleSaveConfig = (configData) => {
    const { currentRowIndex, data } = this.state;
    const { business_type } = this.props;

    if (
      currentRowIndex === null ||
      currentRowIndex < 0 ||
      currentRowIndex >= data.length
    ) {
      console.error("Invalid currentRowIndex:", currentRowIndex);
      return;
    }

    const newData = [...data];
    const currentRow = newData[currentRowIndex];
    const isFnB = business_type === "cafe" || business_type === "restaurant";

    // Calculate total quantity from variants (if any)
    const variants = configData.variants || [];
    let totalVariantQuantity = 0;
    if (variants.length > 0) {
      totalVariantQuantity = variants.reduce((sum, v) => {
        const qty = parseFloat(v.quantity) || 0;
        return sum + qty;
      }, 0);
    }

    // Get current main form stock quantity
    const mainFormQuantity = parseFloat(currentRow.stock_quantity) || 0;

    // Logic: Sync stock quantity between main form and variants
    let finalStockQuantity = currentRow.stock_quantity;

    if (variants.length > 0 && totalVariantQuantity > 0) {
      if (mainFormQuantity > 0 && mainFormQuantity !== totalVariantQuantity) {
        // Case 1: Main form HAS value and it DIFFERS from variant total
        // Warn user and ask if they want to update
        const confirmSync = window.confirm(
          this.props.intl.formatMessage(
            { id: "product.alert.sync-stock-confirm" },
            {
              mainFormQuantity: mainFormQuantity,
              totalVariantQuantity: totalVariantQuantity,
            },
          ),
        );
        if (confirmSync) {
          finalStockQuantity = totalVariantQuantity;
        }
      } else if (!mainFormQuantity || mainFormQuantity === 0) {
        // Case 2: Main form is EMPTY -> Auto-fill with variant total
        finalStockQuantity = totalVariantQuantity;
      }
      // Case 3: Main form equals variant total -> No change needed
    }

    // Merge configData
    // Explicitly handle 'sizes' mapping based on business type
    // If FnB: use 'sizes' (Price List)
    // If Non-FnB: use 'size' (Options List) mapped to 'sizes' for storage consistency
    const updatedRow = {
      ...currentRow,
      ...configData,
      sizes: isFnB ? configData.sizes : configData.size,
      stock_quantity: finalStockQuantity,
    };

    newData[currentRowIndex] = updatedRow;

    this.setState({ data: newData });
  };

  // --- Recipe Config ---
  openRecipe = (index, isAutoOpen = false) => {
    const row = this.state.data[index];
    this.setState({
      recipeModalOpen: true,
      currentRowIndex: index,
      currentRecipeData: {
        ingredients: row.ingredients || [],
        stock_quantity: row.stock_quantity || "",
        stock_cost_price: row.stock_cost_price || "",
      },
      isAutoOpened: isAutoOpen, // Track if opened automatically
    });
  };

  toggleRecipeModal = () => {
    this.setState((prevState) => {
      // Logic: If closing (prevState.recipeModalOpen is true) AND was auto-opened AND no ingredients saved
      if (prevState.recipeModalOpen && prevState.isAutoOpened) {
        const { data, currentRowIndex } = prevState;
        // Check current data in state (which might have been updated by handleRecipeSave, BUT handleRecipeSave closes modal too?)
        // Wait, handleRecipeSave calls setState({data}). calling toggleRecipeModal next?
        // No, handleRecipeSave usually handles its own or expects parent to close?
        // RecipeConfigModal calls props.toggle() after onSave.
        // So toggleRecipeModal IS called.
        // However, we need to know if it was SAVED or CANCELLED.
        // If Saved, data is updated. Ingredient count > 0?
        // If Cancelled, data is NOT updated.
        // But preventing check based on data is tricky if user saved 0 ingredients.
        // Improved Logic:
        // If ingredients are empty in the row -> uncheck.
        // If handleRecipeSave was called, ingredients might be [], but that means they saved empty.
        // If they Cancelled, ingredients matches original.
        // Basically if it ends up empty, we uncheck.

        const row = data[currentRowIndex];
        if (!row.ingredients || row.ingredients.length === 0) {
          const newData = [...data];
          newData[currentRowIndex].is_combo = false;
          // Also reset stock fields to be editable?
          // Previous logic cleared them. If uncheck, they remain cleared?
          // User can re-enter.
          return {
            recipeModalOpen: !prevState.recipeModalOpen,
            isAutoOpened: false,
            data: newData,
          };
        }
      }

      return {
        recipeModalOpen: !prevState.recipeModalOpen,
        isAutoOpened: false,
      };
    });
  };

  handleRecipeSave = (recipeData) => {
    const { currentRowIndex, data } = this.state;
    const newData = [...data];
    const hasIngredients =
      recipeData.ingredients && recipeData.ingredients.length > 0;

    newData[currentRowIndex] = {
      ...newData[currentRowIndex],
      ...recipeData,
      is_combo: hasIngredients, // Auto-sync Combo status
    };
    this.setState({ data: newData });
  };

  // --- Submission ---
  handleSubmit = () => {
    const { data } = this.state;
    let errorMsg = "";
    let validRowsCount = 0;

    // 1. Validation Loop
    for (let i = 0; i < data.length; i++) {
      const r = data[i];
      // Check if row has ANY content
      const hasName = r.name && r.name.trim() !== "";
      const hasCat =
        r.category_id || (r.category_name && r.category_name.trim() !== "");
      const hasPrice = hasInventoryValue(r.price);
      const hasBarcode = hasInventoryValue(r.barcode);
      const hasAnyData = hasName || hasCat || hasPrice || hasBarcode;

      if (!hasAnyData) continue; // Skip completely empty rows

      const missing = [];
      if (!hasName)
        missing.push(
          this.props.intl.formatMessage({
            id: "product.validation.missing-name",
          }),
        );
      if (!hasCat)
        missing.push(
          this.props.intl.formatMessage({
            id: "product.validation.missing-category",
          }),
        );
      if (!hasPrice)
        missing.push(
          this.props.intl.formatMessage({
            id: "product.validation.missing-price",
          }),
        );

      // Validate Unit for Non-FnB
      const isFnB =
        this.props.business_type === "cafe" ||
        this.props.business_type === "restaurant";

      if (!isFnB) {
        if (!r.unit || r.unit.trim() === "")
          missing.push(
            this.props.intl.formatMessage({
              id: "product.validation.missing-unit",
            }),
          );
      }

      // Validate Stock Info - CHỈ CHO NON-FnB
      // F&B (cafe/restaurant) hoặc Hàng Combo không yêu cầu nhập trực tiếp (quản lý qua định lượng)
      const hasVariants = Array.isArray(r.variants) && r.variants.length > 0;
      if (!isFnB && !r.is_combo && !hasVariants) {
        if (!hasInventoryValue(r.stock_quantity))
          missing.push(
            this.props.intl.formatMessage({
              id: "product.validation.missing-import-quantity",
            }),
          );
        if (!hasInventoryValue(r.stock_cost_price))
          missing.push(
            this.props.intl.formatMessage({
              id: "product.validation.missing-cost-price",
            }),
          );
      }

      // Validation Barcode
      if (hasBarcode) {
        const barcode = String(r.barcode).trim();
        if (!/^\d{8,13}$/.test(barcode)) missing.push("Mã vạch phải gồm 8-13 chữ số");

        // 1. Internal Duplicate Check
        const isInternalDup = data.some(
          (item, idx) =>
            idx !== i && hasInventoryValue(item.barcode) && String(item.barcode).trim() === barcode,
        );
        if (isInternalDup) {
          missing.push(
            this.props.intl.formatMessage(
              { id: "product.validation.barcode-duplicate-list" },
              { barcode: barcode },
            ),
          );
        }

        // 2. External (System) Duplicate Check
        if (this.barcodeMap.has(barcode)) {
          missing.push(
            this.props.intl.formatMessage(
              { id: "product.validation.barcode-duplicate-system" },
              { barcode: barcode },
            ),
          );
        }
      }

      if (missing.length > 0) {
        errorMsg += `Dòng ${i + 1}: ${missing.join(", ")}\n`;
      } else {
        validRowsCount++;
      }
    }

    if (errorMsg.length > 0) {
      alert(
        this.props.intl.formatMessage({
          id: "product.alert.validation-check-info",
        }) + errorMsg,
      );
      return;
    }

    if (validRowsCount === 0) {
      alert("Vui lòng nhập ít nhất một sản phẩm hợp lệ!");
      return;
    }

    // 2. Prepare Data (Only take valid rows, which we know have all fields now)
    const validData = data.filter((r) => r.name && r.name.trim() !== "");

    // Normalize data before sending
    const processedData = validData.map((item) => {
      let imageString = "";
      let imagesArray = [];

      // Handle Image: Send both 'image' (string) and 'images' (array)
      if (Array.isArray(item.images)) {
        imagesArray = [...item.images];
        imageString = imagesArray[0] ?? "";
      } else if (Array.isArray(item.image)) {
        imagesArray = [...item.image];
        imageString = imagesArray[0] ?? "";
      } else if (typeof item.image === "string") {
        imageString = item.image;
        if (imageString) imagesArray = [imageString];
      }

      return {
        ...item,
        barcode: String(item.barcode ?? "").trim(),
        image: imageString, // Primary image
        images: imagesArray, // Full list for backend
        sku: item.sku ?? "", // Explicitly pass SKU
        // Map plural fields (Frontend) to singular fields (Backend)
        size: item.sizes ?? item.size ?? [],
        topping: item.toppings ?? item.topping ?? [],
        flavor: item.flavors ?? item.flavor ?? [],
        ice: item.ice ?? [],
        sugar: item.sugar ?? [],
        // Include variant data
        color: item.color ?? [],
        material: item.material ?? [],
        variants: item.variants ?? [],
        __unmatchedImportRows: item.__unmatchedImportRows ?? [],
        // Clean up data based on business_type (F&B doesn't use stock)
        ...(this.props.business_type === "cafe" ||
        this.props.business_type === "restaurant"
          ? {
              stock_quantity: undefined,
              stock_cost_price: undefined,
              stock_supplier: undefined,
              stock_paid: undefined,
              stock_remaining: undefined,
            }
          : {}),
      };
    });

    console.log("Processed data for submission:", processedData);
    const result = this.props.handleImportProduct(processedData);
    if (result && typeof result.then === "function") {
      result.then((res) => {
        if (res && res.failedImports && res.failedImports.length === 0) {
          this.resetForm();
        }
      }).catch(() => {});
    }
  };

  resetForm = () => {
    const freshData = Array.from({ length: 15 }).map(() => this.getEmptyRow());
    this.setState({
      data: freshData,
      createdProviders: [],
      modalOpen: false,
      recipeModalOpen: false,
      currentRowIndex: null,
      currentRowData: null,
      currentRecipeData: null,
    });
    if (this.props.onDataChange) {
      this.props.onDataChange(freshData);
    }
  };

  getUniqueOptions = (field) => {
    const { data_all } = this.props;
    if (!data_all || data_all.length === 0) return [];

    const options = data_all.flatMap((product) => {
      try {
        const val = product[field];
        if (!val) return [];
        // If it's a JSON string
        if (
          typeof val === "string" &&
          (val.startsWith("[") || val.startsWith("{"))
        ) {
          const parsed = JSON.parse(val);
          return Array.isArray(parsed) ? parsed : [parsed];
        }
        // Handle simple string values (e.g. "Size S" or "Note text")
        if (typeof val === "string") {
          return [{ label: val, value: val }];
        }
        // If it's already an array
        if (Array.isArray(val)) return val;
        return [];
      } catch (e) {
        return [];
      }
    });

    const uniqueOptions = [];
    const seenValues = new Set();
    options.forEach((opt) => {
      let value = opt.value || opt.name || opt.label;
      let label = opt.label || opt.name || opt.value;
      if (value && !seenValues.has(value)) {
        seenValues.add(value);
        uniqueOptions.push({ label, value });
      }
    });
    return uniqueOptions;
  };

  render() {
    const { data, categories } = this.state;
    const { business_type } = this.props;

    return (
      <div className="product-entry-table-container">
        {/* Datalist for Product Name Suggestions */}
        <datalist id="product-suggestions">
          {this.props.data_all &&
            this.props.data_all.map((p, i) => (
              <option key={i} value={p.name} />
            ))}
        </datalist>
        <style>{`
          .product-entry-table-container .rt-thead.-header {
            box-shadow: none;
            // border-bottom: 2px solid #dee2e6;
          }
          .product-entry-table-container .rt-th {
            padding: 10px 5px;
            font-weight: 600;
            // background-color: #f8f9fa;
          }
          .product-entry-table-container .rt-td {
            padding: 4px 5px;
            display: flex;
            align-items: center;
          }
          .product-entry-table-container .rt-tr,
          .product-entry-table-container .rt-td {
            outline: none !important;
          }
          .product-entry-table-container input.form-control-sm {
            height: 32px;
          }
          .product-entry-table-container .react-select .react-select__control {
            min-height: 32px;
          }
          .product-entry-table-container .btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
          }
          .product-entry-scroll-toolbar {
            border: 1px solid #e6ebf2;
            border-radius: 12px;
            background: linear-gradient(135deg, #fbfcfe 0%, #f4f8fb 100%);
            padding: 8px 10px;
          }
          .product-entry-scroll-hint {
            display: inline-flex;
            align-items: center;
            color: #51606f;
            font-size: 0.8rem;
            white-space: nowrap;
          }
          .product-entry-top-scroll {
            height: 16px;
            overflow-x: auto;
            overflow-y: hidden;
            flex: 1 1 220px;
            max-width: 520px;
          }
          .product-entry-top-scroll::-webkit-scrollbar {
            height: 8px;
          }
          .product-entry-top-scroll::-webkit-scrollbar-thumb {
            background: #b9c7d6;
            border-radius: 999px;
          }
          .product-entry-table-scroll-shell {
            position: relative;
          }
          .product-entry-table-scroll-shell::after {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 36px;
            height: 100%;
            pointer-events: none;
            background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.94));
          }
          .product-entry-table-drag-area {
            cursor: grab;
          }
          .product-entry-table-container .is-dragging-horizontal {
            cursor: grabbing;
            user-select: none;
          }
        `}</style>

        {/* LEGACY_PRODUCT_ENTRY_LOCAL_SCROLL_AFFORDANCE_START
        <div className="product-entry-scroll-toolbar d-flex flex-wrap align-items-center justify-content-between mb-2">
          ...
        </div>

        <div className="product-entry-table-scroll-shell">
          <PerfectScrollbar
            ref={this.setScrollRef}
            options={{ suppressScrollX: false, suppressScrollY: false }}
            style={{ width: "100%" }}
          >
            <div
              ref={this.tableInnerRef}
              className="product-entry-table-drag-area"
              style={{ minWidth: "100%", width: "max-content" }}
              onMouseDown={this.handleDragScrollStart}
            >
        LEGACY_PRODUCT_ENTRY_LOCAL_SCROLL_AFFORDANCE_END */}
        <HorizontalScrollAffordance
          minWidth={1800}
          hint="Kéo ngang vùng bảng hoặc dùng nút trượt để xem các cột phía sau."
        >
          <ReactTable
            data={data}
            columns={this.getColumns()}
            categories={categories}
            defaultPageSize={15}
            showPageSizeOptions={false}
            showPagination={data.length > 15}
            minRows={1}
            className="-striped -highlight"
            PaginationComponent={Pagination}
          />
        </HorizontalScrollAffordance>
        {/* LEGACY_PRODUCT_ENTRY_LOCAL_SCROLL_AFFORDANCE_CLOSE_START
            </div>
          </PerfectScrollbar>
        </div>
        LEGACY_PRODUCT_ENTRY_LOCAL_SCROLL_AFFORDANCE_CLOSE_END */}

        <div className="mt-3 d-flex justify-content-between">
          <Button
            color="outline-secondary"
            size="sm"
            onClick={this.handleAddRow}
          >
            <i className="simple-icon-plus mr-1" /> Thêm dòng
          </Button>
          <Button color="primary" onClick={this.handleSubmit}>
            <i className="simple-icon-check mr-2" />
            Xác nhận thêm ({data.filter((x) => x.name).length})
          </Button>
        </div>

        <ProductVariantConfigModal
          isOpen={this.state.modalOpen}
          toggle={this.toggleModal}
          data={this.state.currentRowData}
          baseUnit={this.state.currentRowData?.unit || ""}
          basePrice={this.state.currentRowData?.price ?? ""}
          baseCostPrice={this.state.currentRowData?.stock_cost_price ?? ""}
          onSave={this.handleSaveConfig}
          business_type={this.props.business_type}
          allSizeOptions={this.getUniqueOptions("size")}
          allColorOptions={this.getUniqueOptions("color")}
          allMaterialOptions={this.getUniqueOptions("material")}
        />

        <RecipeConfigModal
          isOpen={this.state.recipeModalOpen}
          toggle={this.toggleRecipeModal}
          data={this.state.currentRecipeData}
          onSave={this.handleRecipeSave}
          business_type={business_type}
          data_stocks={this.props.data_stocks || []}
          onCreateStock={this.props.onCreateStock}
          sellingPrice={parseFloat(
            this.state.data[this.state.currentRowIndex]?.price || 0,
          )}
          data_providers={this.props.data_providers || []}
        />

        <input
          type="file"
          accept="image/*"
          multiple
          ref={this.fileInputRef}
          style={{ display: "none" }}
          onChange={this.onFileChange}
        />
      </div>
    );
  }
}

export default injectIntl(ProductEntryModal);
